(function () {
  'use strict';

  const sel = (root, s) => root.querySelector(s);
  const selAll = (root, s) => Array.from(root.querySelectorAll(s));

  const parseParams = (params) => {
    // params is an array of strings; trim leading ? & and join with &
    return (
      params
        .map((p) => (p || '').toString().trim())
        .filter(Boolean)
        // Remove any double quotes that might have leaked from Liquid/HTML contexts
        .map((p) => p.replace(/"/g, ''))
        .map((p) => p.replace(/^\??&?/, ''))
        .join('&')
    );
  };

  class MatchmakerSection {
    constructor(section) {
      this.section = section;
      this.intro = sel(section, '.bbd-matchmaker__intro');
      this.steps = sel(section, '.bbd-matchmaker__steps');
      this.templatesRoot = sel(section, '.bbd-matchmaker__templates');
      this.startQuestionHandle = this.steps?.dataset.startQuestion || '';
      this.resultsUrl = this.steps?.dataset.resultsUrl || '/collections/all';

      this.historyKey = `mm-${section.id}`;

      this.state = {
        current: null, // current question handle
        path: [], // stack of question handles visited
        answers: {}, // map questionHandle -> Set(answerHandles)
        params: [] // accumulated filter params
      };

      // Mark instance on the section to avoid double initialization
      this.section.__bbdMM = this;

      this.#bind();
      this.#restoreFromUrlOrIntro();
    }

    #bind() {
      // Detect when user is navigating away (to results page).
      // Uses pagehide instead of beforeunload: functionally identical for this sessionStorage
      // write, but beforeunload listeners make the page ineligible for the back/forward cache.
      window.addEventListener('pagehide', () => {
        const returnFlagKey = `${this.historyKey}-returning`;
        sessionStorage.setItem(returnFlagKey, 'true');
      });

      // Start button
      this.section.addEventListener('click', (e) => {
        const startBtn = e.target.closest('.bbd-matchmaker__start');
        if (startBtn) {
          this.start();
        }
      });

      // Answer click
      this.section.addEventListener('click', (e) => {
        const li = e.target.closest('.bbd-matchmaker__answer');
        if (!li || !this.steps.contains(li)) return;
        const button = li.querySelector('.bbd-matchmaker__answer-button');
        if (!button) return;
        const stepEl = e.target.closest('.bbd-matchmaker__step');
        if (!stepEl) return;
        this.#toggleAnswer(stepEl, li);
      });

      // Prev/Next
      this.section.addEventListener('click', (e) => {
        const prev = e.target.closest('.bbd-matchmaker__prev');
        const next = e.target.closest('.bbd-matchmaker__next');
        const stepEl = e.target.closest('.bbd-matchmaker__step');
        if (prev && stepEl) {
          this.prev();
        } else if (next && stepEl) {
          this.next();
        }
      });

      // History navigation
      window.addEventListener('popstate', (event) => {
        const st = event.state?.[this.historyKey];
        if (!st) return;
        this.#applyState(st, { push: false });
      });
    }

    #restoreFromUrlOrIntro() {
      // Check if we're returning from results page using sessionStorage flag set by pagehide
      const returnFlagKey = `${this.historyKey}-returning`;
      const isReturning = sessionStorage.getItem(returnFlagKey);

      if (isReturning) {
        sessionStorage.removeItem(returnFlagKey);
        // Reset state and URL to show intro
        this.state = { current: null, path: [], answers: {}, params: [] };
        const url = new URL(window.location.href);
        url.searchParams.delete('mm');
        url.searchParams.delete('mm_sel');
        history.replaceState({}, '', url);
        this.#showIntro();
        return;
      }

      // If URL contains mm=<questionHandle>, open in steps mode, else show intro
      const url = new URL(window.location.href);
      const mm = url.searchParams.get('mm');

      if (mm) {
        this.start(mm);
        // Try to restore selected answers for this question if present
        const selStr = url.searchParams.get('mm_sel');
        if (selStr) {
          const selected = selStr.split(',').filter(Boolean);
          const stepEl = sel(this.steps, '.bbd-matchmaker__step');
          if (stepEl) {
            for (const handle of selected) {
              const li = sel(stepEl, `.bbd-matchmaker__answer[data-answer="${CSS.escape(handle)}"]`);
              if (li) this.#setAnswerSelected(li, true);
            }
            this.#updateNextEnabled(stepEl);
          }
        }
      } else {
        this.#showIntro();
      }
    }

    start(startHandle) {
      // Read the live handle from the DOM each time (Theme Editor may update settings after init)
      const liveHandle = this.steps?.dataset.startQuestion || '';
      const handle = startHandle || liveHandle || this.startQuestionHandle;
      if (!handle) {
        // No start handle – keep intro visible and disable start to signal misconfiguration
        this.#showIntro();
        this.steps.hidden = true;
        this.section.querySelector('.bbd-matchmaker__start')?.setAttribute('disabled', 'true');
        return;
      }
      this.#hideIntro();
      this.steps.hidden = false;
      this.state = { current: handle, path: [handle], answers: {}, params: [] };
      this.#renderQuestion(handle);
      this.#pushHistory();
    }

    prev() {
      if (this.state.path.length <= 1) {
        // Back to intro
        this.#showIntro();
        this.steps.hidden = true;
        // Update URL remove mm param
        const url = new URL(window.location.href);
        url.searchParams.delete('mm');
        url.searchParams.delete('mm_sel');
        history.pushState({}, '', url);
        return;
      }
      // Pop current
      this.state.path.pop();
      const prevHandle = this.state.path[this.state.path.length - 1];
      this.state.current = prevHandle;
      this.#renderQuestion(prevHandle);
      // Restore selections for this question
      const stepEl = sel(this.steps, '.bbd-matchmaker__step');
      const selectedSet = this.state.answers[prevHandle] || new Set();
      for (const li of selAll(stepEl, '.bbd-matchmaker__answer')) {
        const handle = li.dataset.answer;
        this.#setAnswerSelected(li, selectedSet.has(handle));
      }
      this.#updateNextEnabled(stepEl);
      this.#pushHistory();
    }

    next() {
      const stepEl = sel(this.steps, '.bbd-matchmaker__step');
      if (!stepEl) return;
      const qHandle = stepEl.dataset.question;
      const selected = selAll(stepEl, '.bbd-matchmaker__answer-button[aria-pressed="true"]').map((btn) =>
        btn.closest('.bbd-matchmaker__answer')
      );
      if (!selected.length) return;

      // Save selections
      const set = new Set(selected.map((li) => li.dataset.answer));
      this.state.answers[qHandle] = set;

      // Update params from these selections (dedupe)
      for (const li of selected) {
        const p = (li.dataset.param || '').trim();
        if (!p) continue;
        if (!this.state.params.includes(p)) this.state.params.push(p);
      }

      // Determine next question (first with child)
      let nextHandle = null;
      for (const li of selected) {
        const child = (li.dataset.child || '').trim();
        if (child) {
          nextHandle = child;
          break;
        }
      }

      if (nextHandle) {
        this.state.current = nextHandle;
        this.state.path.push(nextHandle);
        this.#renderQuestion(nextHandle);
        this.#pushHistory();
      } else {
        this.#finish();
      }
    }

    #toggleAnswer(stepEl, li) {
      const multi = stepEl.dataset.multiple === 'true' || stepEl.dataset.multiple === '1';
      const button = li.querySelector('.bbd-matchmaker__answer-button');
      if (!multi) {
        // Clear others
        selAll(stepEl, '.bbd-matchmaker__answer-button[aria-pressed="true"]').forEach((b) =>
          b.setAttribute('aria-pressed', 'false')
        );
        button.setAttribute('aria-pressed', 'true');
      } else {
        const pressed = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      }
      this.#updateNextEnabled(stepEl);
      this.#updateUrlForCurrentStep(stepEl);
    }

    #updateNextEnabled(stepEl) {
      const nextBtn = sel(stepEl, '.bbd-matchmaker__next');
      if (!nextBtn) return;
      const any = sel(stepEl, '.bbd-matchmaker__answer-button[aria-pressed="true"]');
      nextBtn.disabled = !any;
    }

    #updateUrlForCurrentStep(stepEl) {
      // For history navigation, store mm and selected answer handles in URL
      const qHandle = stepEl.dataset.question;
      const selectedHandles = selAll(stepEl, '.bbd-matchmaker__answer-button[aria-pressed="true"]').map(
        (b) => b.closest('.bbd-matchmaker__answer').dataset.answer
      );
      const url = new URL(window.location.href);
      url.searchParams.set('mm', qHandle);
      if (selectedHandles.length) url.searchParams.set('mm_sel', selectedHandles.join(','));
      else url.searchParams.delete('mm_sel');
      history.replaceState(history.state, '', url);
    }

    #renderQuestion(handle) {
      const tpl = sel(this.templatesRoot, `#mm-question-${CSS.escape(handle)}`);
      this.steps.innerHTML = '';
      if (!tpl) {
        this.steps.hidden = true;
        this.#showIntro();
        return;
      }
      const content = tpl.content.cloneNode(true);
      this.steps.appendChild(content);
    }

    #finish() {
      // Build query and redirect to results collection
      const baseUrl = this.resultsUrl || '/collections/all';
      const url = new URL(baseUrl, window.location.origin);
      const cleanedParams = this.state.params.map((q) => q.replace('[', '').replace(']', '').replace('"', ''));
      const appended = parseParams(cleanedParams);
      if (appended) {
        // Merge with existing params on baseUrl
        const existing = url.search ? url.search.slice(1) : '';
        const merged = [existing, appended].filter(Boolean).join('&');
        url.search = merged ? `?${merged}` : '';
      }
      window.location.assign(url.toString());
    }

    #pushHistory() {
      const url = new URL(window.location.href);
      url.searchParams.set('mm', this.state.current);
      url.searchParams.delete('mm_sel');
      const st = {
        [this.historyKey]: {
          current: this.state.current,
          path: [...this.state.path],
          answers: Object.fromEntries(Object.entries(this.state.answers).map(([k, v]) => [k, Array.from(v)])),
          params: [...this.state.params]
        }
      };
      history.pushState(st, '', url);
    }

    #applyState(st, { push }) {
      this.state.current = st.current;
      this.state.path = [...st.path];
      this.state.answers = Object.fromEntries(Object.entries(st.answers || {}).map(([k, arr]) => [k, new Set(arr)]));
      this.state.params = [...(st.params || [])];
      this.#renderQuestion(this.state.current);
      const stepEl = sel(this.steps, '.bbd-matchmaker__step');
      const selectedSet = this.state.answers[this.state.current] || new Set();
      for (const li of selAll(stepEl, '.bbd-matchmaker__answer')) {
        const handle = li.dataset.answer;
        this.#setAnswerSelected(li, selectedSet.has(handle));
      }
      this.#updateNextEnabled(stepEl);
      if (push) this.#pushHistory();
    }

    #showIntro() {
      this.intro.hidden = false;
    }
    #hideIntro() {
      this.intro.hidden = true;
    }

    #setAnswerSelected(li, selected) {
      const button = li.querySelector('.bbd-matchmaker__answer-button');
      if (button) button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
  }

  const init = (root = document) => {
    selAll(root, '.bbd-matchmaker').forEach((section) => {
      if (section.__bbdMM) return; // already initialized
      new MatchmakerSection(section);
    });
  };

  // Expose for testing (Jest/jsdom) without affecting storefront
  // In browsers, `module` is undefined, so this is tree‑safe.
  if (typeof window !== 'undefined') {
    window.__BBD_MM__ = { MatchmakerSection, parseParams, init };
  }
  if (typeof module !== 'undefined' && module.exports) {
    // CommonJS export for Jest environment
    module.exports = { MatchmakerSection, parseParams, init };
  }

  // Guard all DOM-side bootstrapping so requiring this file in Node (Jest) doesn't touch the DOM
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    // Initialize on initial load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init(document));
    } else {
      init(document);
    }

    // Initialize when sections are loaded in the Theme Editor
    document.addEventListener('shopify:section:load', (event) => {
      const container = event.target || document;
      init(container);
    });

    // Cleanup marker on unload so re-loading can re-init
    document.addEventListener('shopify:section:unload', (event) => {
      const container = event.target || document;
      selAll(container, '.bbd-matchmaker').forEach((section) => {
        section.__bbdMM = null;
      });
    });
  }
})();
